<template>
  <section v-if="modelValue" class="my-8">
    <table v-if="validLocation" class="core-office-table w-full">
      <tbody>
        <tr class="py-4">
          <td>
            {{ wt('summary', 'costRate') }}
          </td>
          <td class="font-bold text-lg font-medium">
            <span class="mr-2">{{ hourlyRateFormatted }}</span>
            <span class="mr-2">{{ $t('general.currency_per_hour') }}</span>
            {{ $t('general.excl_tax') }}
          </td>
        </tr>
        <tr class="border-b border-neutral-300 pb-8">
          <td>
            {{ wt('summary', 'addCostRate') }}
          </td>
          <td class="font-bold text-lg font-medium">
            <input
              v-if="canEdit"
              v-model="modelValue.hourlyRateAdjustment"
              id="hourlyRateAdjustment"
              type="number"
              class="core-form-input mb-0 h-10 w-full font-regular text-primary-dark"
              @blur.prevent="onBlurHourlyRateAdjustment($event)" />
            <span v-else>{{ modelValue.hourlyRateAdjustment }}</span>
            <span class="ml-4">{{ $t('general.currency_per_hour') }}</span>
          </td>
        </tr>
        <tr class="mt-4 py-6">
          <td>{{ wt('summary', 'subtotal') }}</td>
          <td class="font-bold text-lg font-medium">
            <span class="mr-2">{{ adjustedHourlyRateFormatted }}</span>
            <span class="mr-2">{{ $t('general.currency_per_hour') }}</span>
            {{ $t('general.excl_tax') }}
          </td>
        </tr>
        <tr>
          <td>{{ wt('summary', 'risk') }}</td>
          <td class="font-bold text-lg font-medium">
            {{ riskLevelKey ? wt('risk', 'option', riskLevelKey) : '' }}
          </td>
        </tr>
        <tr class="border-b border-neutral-300 pt-6 pb-8">
          <td>
            {{ wt('summary', 'addRisk') }}
            <br />
            {{ wt('summary', 'addRiskRef') }}
          </td>
          <td class="font-bold text-lg font-medium">
            <input
              v-if="canEdit"
              v-model="modelValue.riskAdjustment"
              id="riskAdjustment"
              type="number"
              class="core-form-input mb-0 h-10 w-full font-regular text-primary-dark"
              @blur.prevent="onBlurRiskAdjustment($event)" />
            <span v-else>{{ modelValue.riskAdjustment }}</span>
            <span class="ml-2" :class="{ 'ml-12': canEdit }">%</span>
          </td>
        </tr>
        <tr class="py-6">
          <td>{{ wt('summary', 'sum') }}</td>
          <td class="font-bold text-lg font-medium">
            <span class="mr-2">{{ selectedHourlyRateFormatted }}</span>
            <span class="mr-2">{{ $t('general.currency_per_hour') }}</span>
            {{ $t('general.excl_tax') }}
          </td>
        </tr>
      </tbody>
    </table>

    <section v-else class="core-alert-zone my-24">
      {{ wt('location', 'hint', 'disabled') }}
    </section>
  </section>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import { useOfficeCalculator } from '@/stores/officeCenter/officeCalculator'
import { useOfficeStore } from '@/stores/office'
import { formatCHF } from '@/helpers/format'
import { useWidgetText } from '@/helpers/useWidgetText'

interface OfficeSummaryForm {
  hourlyRateAdjustment: number
  riskAdjustment: number
}

const { wt } = useWidgetText('widget-texts-office')
const calculator = useOfficeCalculator()
const store = useOfficeStore()
const modelValue = defineModel({ type: Object as PropType<OfficeSummaryForm> })

const validLocation = computed(() => {
  return calculator.getOffice?.canton
})

const canEdit = computed(() => {
  return store.getMeta?.canEdit
})

const hourlyRateFormatted = computed((): string => {
  const rate = calculator?.getHourlyRate || 0
  return formatCHF(rate)
})

const adjustedHourlyRateFormatted = computed((): string => {
  const rate = calculator?.getAdjustedHourlyRate || 0
  return formatCHF(rate)
})
const selectedHourlyRateFormatted = computed((): string => {
  const rate = calculator?.getSelectedHourlyRate || 0
  return formatCHF(rate)
})

const riskLevelKey = computed((): string | null => {
  return calculator?.getRiskLevel || null
})

const onBlurHourlyRateAdjustment = function (event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value.replaceAll("'", ''))
  if (isNaN(value) && modelValue?.value) {
    modelValue.value.hourlyRateAdjustment = 0
  }
}
const onBlurRiskAdjustment = function (event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value.replaceAll("'", ''))
  if (isNaN(value) && modelValue?.value) {
    modelValue.value.riskAdjustment = 0
  }
}
</script>
<style class="postcss">
.core-office-table {
  & tr {
    @apply grid gap-4 md:grid-cols-2;
  }

  & td {
    @apply flex items-center text-neutral-650;
  }
}
</style>
